import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API_BASE = "https://api.welbeing.in";

const SHOP_BY_OCCASION_NAMES = [
    "shop by occasions",
    "shop by occasion",
];

const API = {
    banners: `${API_BASE}/home/banners/`,
    categories: `${API_BASE}/admin/categories/categories/`,
    subcategories: `${API_BASE}/admin/categories/subcategories/`,
    addBanner: `${API_BASE}/admin/home/addbanner/`,
    deleteBanner: `${API_BASE}/home/delete-sectionbanner/`,
};

const getErrorMessage = (err) => {
    if (axios.isCancel(err) || err?.code === "ERR_CANCELED") {
        return "Request was canceled.";
    }

    const status = err?.response?.status;
    const responseData = err?.response?.data;

    const serverMessage =
        responseData?.message ||
        responseData?.error ||
        responseData?.detail;

    if (status === 500) {
        return serverMessage || "Server error (500). Please try again later.";
    }

    if (status === 404) {
        return serverMessage || "API endpoint not found (404).";
    }

    if (status === 401 || status === 403) {
        return serverMessage || "You are not authorized to perform this action.";
    }

    if (status) {
        return serverMessage || `Request failed with status ${status}.`;
    }

    if (err?.request) {
        return "No response received from the server. Check your connection.";
    }

    return err?.message || "Something went wrong.";
};

const getArrayFromResponse = (response, label) => {
    const body = response?.data;

    if (Array.isArray(body)) {
        return body;
    }

    if (body?.status === "success" && Array.isArray(body.data)) {
        return body.data;
    }

    if (body?.status === "success" && body.data == null) {
        return [];
    }

    throw new Error(`${label} API returned an unexpected response.`);
};

const logApiError = (label, err) => {
    console.error(`[MultipleBanner] ${label}`, {
        url: err?.config?.url,
        method: err?.config?.method,
        status: err?.response?.status,
        response: err?.response?.data,
        message: err?.message,
    });
};

const MultipleBanner = () => {
    const [banners, setBanners] = useState([]);
    const [occasionSubcategories, setOccasionSubcategories] = useState([]);
    const [subId, setSubId] = useState("");

    const [images, setImages] = useState([]);
    const [previews, setPreviews] = useState([]);

    const [loading, setLoading] = useState(true);
    const [adding, setAdding] = useState(false);
    const [addingProgress, setAddingProgress] = useState("");

    const [deletingIds, setDeletingIds] = useState([]);
    const [successMessage, setSuccessMessage] = useState("");
    const [error, setError] = useState("");

    const fileInputRef = useRef(null);
    const uploadingRef = useRef(false);
    const deletingRef = useRef(new Set());
    const previewUrlsRef = useRef(new Set());

    // Revoke temporary image preview URLs when the component unmounts.
    useEffect(() => {
        const urls = previewUrlsRef.current;

        return () => {
            urls.forEach((url) => URL.revokeObjectURL(url));
            urls.clear();
        };
    }, []);

    const clearPreviews = () => {
        previewUrlsRef.current.forEach((url) => {
            URL.revokeObjectURL(url);
        });

        previewUrlsRef.current.clear();
        setPreviews([]);
        setImages([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // GET banners using Axios.
    const fetchBanners = useCallback(async (signal) => {
        try {
            const response = await axios.get(API.banners, { signal });
            const data = getArrayFromResponse(response, "Banners");

            setBanners(data);
            return data;
        } catch (err) {
            logApiError("Fetch banners", err);
            throw err;
        }
    }, []);

    // GET categories and subcategories using Axios.
    const fetchOccasionSubcategories = useCallback(async (signal) => {
        try {
            const [categoriesRes, subcategoriesRes] = await Promise.all([
                axios.get(API.categories, { signal }),
                axios.get(API.subcategories, { signal }),
            ]);

            const categories = getArrayFromResponse(
                categoriesRes,
                "Categories"
            );

            const subcategories = getArrayFromResponse(
                subcategoriesRes,
                "Subcategories"
            );

            const occasionCategory = categories.find((category) =>
                SHOP_BY_OCCASION_NAMES.includes(
                    String(category.cate || "").trim().toLowerCase()
                )
            );

            if (!occasionCategory) {
                setOccasionSubcategories([]);
                return [];
            }

            const filtered = subcategories.filter(
                (subcategory) =>
                    String(subcategory.cat_id) ===
                    String(occasionCategory.cat_id)
            );

            setOccasionSubcategories(filtered);
            return filtered;
        } catch (err) {
            logApiError("Fetch categories/subcategories", err);
            throw err;
        }
    }, []);

    // Load banners and categories independently.
    const loadData = useCallback(
        async (signal) => {
            setLoading(true);
            setError("");

            const results = await Promise.allSettled([
                fetchBanners(signal),
                fetchOccasionSubcategories(signal),
            ]);

            if (signal?.aborted) {
                return;
            }

            const failures = results
                .filter((result) => result.status === "rejected")
                .map((result) => getErrorMessage(result.reason));

            if (failures.length > 0) {
                setError(failures.join(" "));
            }

            setLoading(false);
        },
        [fetchBanners, fetchOccasionSubcategories]
    );

    useEffect(() => {
        const controller = new AbortController();

        loadData(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadData]);

    // Handle image selection and create preview URLs.
    const handleImageChange = (event) => {
        const selectedFiles = Array.from(event.target.files || []);

        setError("");
        setSuccessMessage("");

        if (selectedFiles.length === 0) {
            return;
        }

        const invalidFiles = selectedFiles.filter(
            (file) => !file.type.startsWith("image/")
        );

        if (invalidFiles.length > 0) {
            event.target.value = "";
            setError("Please select image files only.");
            return;
        }

        const maxSize = 10 * 1024 * 1024;

        if (selectedFiles.some((file) => file.size > maxSize)) {
            event.target.value = "";
            setError("Each image must be smaller than 10 MB.");
            return;
        }

        clearPreviews();

        const newPreviews = selectedFiles.map((file) => {
            const url = URL.createObjectURL(file);
            previewUrlsRef.current.add(url);

            return {
                file,
                url,
                name: file.name,
            };
        });

        setImages(selectedFiles);
        setPreviews(newPreviews);
    };

    // Remove a single image from the preview.
    const handleRemovePreview = (index) => {
        const removedPreview = previews[index];

        if (removedPreview) {
            URL.revokeObjectURL(removedPreview.url);
            previewUrlsRef.current.delete(removedPreview.url);
        }

        setPreviews((previous) =>
            previous.filter((_, i) => i !== index)
        );

        setImages((previous) =>
            previous.filter((_, i) => i !== index)
        );

        setError("");

        if (images.length === 1 && fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    // Upload images sequentially using Axios.
    const handleSubmit = async (event) => {
        event.preventDefault();

        if (uploadingRef.current) {
            return;
        }

        setError("");
        setSuccessMessage("");

        if (!subId) {
            setError("Please select a subcategory.");
            return;
        }

        if (images.length === 0) {
            setError("Please select at least one image.");
            return;
        }

        uploadingRef.current = true;
        setAdding(true);

        let successCount = 0;
        const failedUploads = [];

        // Copy the current selections before starting the upload loop.
        const selectedImages = [...images];
        const selectedPreviews = [...previews];

        try {
            for (let i = 0; i < selectedImages.length; i++) {
                const file = selectedImages[i];

                setAddingProgress(
                    `Uploading ${i + 1} of ${selectedImages.length}…`
                );

                try {
                    const formData = new FormData();

                    formData.append("image", file);
                    formData.append("sub_id", subId);

                    // Axios automatically handles FormData in the browser.
                    const response = await axios.post(
                        API.addBanner,
                        formData,
                        {
                            onUploadProgress: (progressEvent) => {
                                if (progressEvent.total) {
                                    const percent = Math.round(
                                        (progressEvent.loaded /
                                            progressEvent.total) *
                                            100
                                    );

                                    setAddingProgress(
                                        `Uploading ${i + 1} of ${selectedImages.length}… ${percent}%`
                                    );
                                }
                            },
                        }
                    );

                    const data = response.data;

                    if (
                        data?.status &&
                        data.status !== "success" &&
                        data.status !== "0000"
                    ) {
                        throw new Error(
                            data.message || data.error || "Upload failed."
                        );
                    }

                    successCount++;
                } catch (err) {
                    logApiError(`Upload image: ${file.name}`, err);

                    failedUploads.push({
                        name: file.name,
                        message: getErrorMessage(err),
                    });
                }
            }

            // Refresh banner data after all upload attempts.
            try {
                await fetchBanners();
            } catch (err) {
                logApiError("Refresh banners after upload", err);

                setError(
                    `Upload finished, but the banner list could not refresh: ${getErrorMessage(err)}`
                );
            }

            if (successCount > 0) {
                setSuccessMessage(
                    `${successCount} banner${
                        successCount === 1 ? "" : "s"
                    } added successfully.` +
                        (failedUploads.length > 0
                            ? ` ${failedUploads.length} upload${
                                  failedUploads.length === 1 ? "" : "s"
                              } failed.`
                            : "")
                );

                if (failedUploads.length === 0) {
                    clearPreviews();
                    setSubId("");
                } else {
                    const failedNames = new Set(
                        failedUploads.map((item) => item.name)
                    );

                    // Preserve files whose upload failed so they can be retried.
                    const failedPreviews = selectedPreviews.filter(
                        (preview) => failedNames.has(preview.name)
                    );

                    const failedImages = selectedImages.filter((file) =>
                        failedNames.has(file.name)
                    );

                    selectedPreviews.forEach((preview) => {
                        if (!failedNames.has(preview.name)) {
                            URL.revokeObjectURL(preview.url);
                            previewUrlsRef.current.delete(preview.url);
                        }
                    });

                    setPreviews(failedPreviews);
                    setImages(failedImages);

                    if (fileInputRef.current) {
                        fileInputRef.current.value = "";
                    }
                }
            }

            if (failedUploads.length > 0) {
                setError(
                    failedUploads
                        .map(
                            (item) => `${item.name}: ${item.message}`
                        )
                        .join(" ")
                );
            } else if (successCount === 0) {
                setError("All uploads failed. Please try again.");
            }
        } catch (err) {
            logApiError("Upload banners", err);
            setError(getErrorMessage(err));
        } finally {
            uploadingRef.current = false;
            setAdding(false);
            setAddingProgress("");
        }
    };

    // Delete a banner using Axios.
    const handleDelete = async (id) => {
        if (id == null || deletingRef.current.has(String(id))) {
            return;
        }

        if (
            !window.confirm(
                "Are you sure you want to remove this banner?"
            )
        ) {
            return;
        }

        const key = String(id);

        deletingRef.current.add(key);

        setDeletingIds((previous) => [...previous, key]);
        setError("");
        setSuccessMessage("");

        try {
            const response = await axios.post(
                API.deleteBanner,
                { id },
                {
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            const data = response.data;

            if (
                data?.status &&
                data.status !== "success" &&
                data.status !== "0000"
            ) {
                throw new Error(
                    data.message || data.error || "Failed to delete banner."
                );
            }

            setBanners((previous) =>
                previous.filter(
                    (banner) =>
                        String(banner.id) !== key
                )
            );

            setSuccessMessage("Banner removed successfully!");
        } catch (err) {
            logApiError("Delete banner", err);
            setError(getErrorMessage(err));
        } finally {
            deletingRef.current.delete(key);

            setDeletingIds((previous) =>
                previous.filter(
                    (deletingId) => String(deletingId) !== key
                )
            );
        }
    };

    const handleReload = () => {
        const controller = new AbortController();
        loadData(controller.signal);
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            {successMessage && (
                                <div
                                    className="alert alert-success alert-dismissible fade show"
                                    role="status"
                                >
                                    {successMessage}

                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() =>
                                            setSuccessMessage("")
                                        }
                                    />
                                </div>
                            )}

                            {error && (
                                <div
                                    className="alert alert-danger alert-dismissible fade show"
                                    role="alert"
                                >
                                    {error}

                                    <button
                                        type="button"
                                        className="btn-close"
                                        aria-label="Close"
                                        onClick={() => setError("")}
                                    />
                                </div>
                            )}

                            <div className="mb-3">
                                <h5
                                    className="mb-0 fw-bold d-inline-block px-3 py-2 rounded"
                                    style={{
                                        fontSize: "30px",
                                        background:
                                            "linear-gradient(135deg, #0d6efd15, #0d6efd08)",
                                        borderLeft: "5px solid #0d6efd",
                                        color: "#0d6efd",
                                        letterSpacing: "0.3px",
                                    }}
                                >
                                    Add Main Banners
                                </h5>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <div className="form-body">
                                    <div className="row align-items-end">
                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="subCategory"
                                                    className="form-label"
                                                >
                                                    Sub Category (Shop By
                                                    Occasions)
                                                </label>

                                                <select
                                                    id="subCategory"
                                                    className="form-select"
                                                    name="sub_category"
                                                    value={subId}
                                                    onChange={(event) => {
                                                        setSubId(
                                                            event.target.value
                                                        );
                                                        setError("");
                                                    }}
                                                    disabled={adding}
                                                >
                                                    <option value="">
                                                        -- Select Sub Category --
                                                    </option>

                                                    {occasionSubcategories.map(
                                                        (sub) => (
                                                            <option
                                                                key={sub.sub_id}
                                                                value={sub.sub_id}
                                                            >
                                                                {sub.sub_cate}
                                                            </option>
                                                        )
                                                    )}
                                                </select>

                                                {!loading &&
                                                    occasionSubcategories.length ===
                                                        0 && (
                                                        <small className="text-muted d-block mt-1">
                                                            No occasion
                                                            subcategories found.
                                                        </small>
                                                    )}
                                            </div>
                                        </div>

                                        <div className="col-md-4">
                                            <div className="mb-3">
                                                <label
                                                    htmlFor="bannerImages"
                                                    className="form-label"
                                                >
                                                    Banner Images
                                                    <span
                                                        className="text-muted ms-1"
                                                        style={{
                                                            fontSize: "0.78rem",
                                                        }}
                                                    >
                                                        (select multiple)
                                                    </span>
                                                </label>

                                                <input
                                                    id="bannerImages"
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    multiple
                                                    ref={fileInputRef}
                                                    onChange={handleImageChange}
                                                    disabled={adding}
                                                />

                                                {images.length > 0 && (
                                                    <span className="badge bg-primary mt-1">
                                                        {images.length} image
                                                        {images.length === 1
                                                            ? ""
                                                            : "s"}{" "}
                                                        selected
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="mb-3">
                                                <button
                                                    type="submit"
                                                    className="btn btn-light-primary text-primary font-medium w-100"
                                                    disabled={adding || loading}
                                                >
                                                    {adding ? (
                                                        <>
                                                            <span
                                                                className="spinner-border spinner-border-sm me-2"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                            {addingProgress ||
                                                                "Adding…"}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ti ti-plus me-1" />
                                                            Add
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {previews.length > 0 && (
                                        <div className="row mb-3">
                                            <div className="col-12">
                                                <label className="mb-2 fw-semibold">
                                                    Preview ({previews.length}{" "}
                                                    image
                                                    {previews.length === 1
                                                        ? ""
                                                        : "s"}
                                                    )
                                                </label>

                                                <div className="d-flex flex-wrap gap-3">
                                                    {previews.map(
                                                        (item, index) => (
                                                            <div
                                                                key={item.url}
                                                                className="position-relative"
                                                                style={{
                                                                    display:
                                                                        "inline-block",
                                                                }}
                                                            >
                                                                <img
                                                                    src={item.url}
                                                                    alt={`Preview of ${item.name}`}
                                                                    style={{
                                                                        width: "120px",
                                                                        height: "75px",
                                                                        objectFit:
                                                                            "cover",
                                                                        borderRadius:
                                                                            "8px",
                                                                        border: "1px solid #dee2e6",
                                                                        display:
                                                                            "block",
                                                                    }}
                                                                />

                                                                <span
                                                                    className="badge bg-dark"
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        bottom: "4px",
                                                                        left: "4px",
                                                                        fontSize:
                                                                            "0.65rem",
                                                                    }}
                                                                >
                                                                    {index + 1}
                                                                </span>

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger"
                                                                    aria-label={`Remove ${item.name}`}
                                                                    disabled={
                                                                        adding
                                                                    }
                                                                    style={{
                                                                        position:
                                                                            "absolute",
                                                                        top: "-8px",
                                                                        right: "-8px",
                                                                        borderRadius:
                                                                            "50%",
                                                                        width: "24px",
                                                                        height: "24px",
                                                                        padding: 0,
                                                                    }}
                                                                    onClick={() =>
                                                                        handleRemovePreview(
                                                                            index
                                                                        )
                                                                    }
                                                                >
                                                                    <i className="ti ti-x" />
                                                                </button>

                                                                <small
                                                                    className="d-block text-truncate mt-1"
                                                                    style={{
                                                                        maxWidth:
                                                                            "120px",
                                                                    }}
                                                                    title={
                                                                        item.name
                                                                    }
                                                                >
                                                                    {item.name}
                                                                </small>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </form>

                            <hr />

                            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                                <h5 className="mb-0">
                                    Section Banners List
                                </h5>

                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleReload}
                                        disabled={loading}
                                    >
                                        <i className="ti ti-refresh me-1" />
                                        {loading ? "Loading…" : "Reload"}
                                    </button>

                                    <span className="badge bg-dark fs-6">
                                        {banners.length}{" "}
                                        {banners.length === 1
                                            ? "Banner"
                                            : "Banners"}
                                    </span>
                                </div>
                            </div>

                            <div
                                className="table-responsive"
                                style={{ overflowX: "auto" }}
                            >
                                <table className="table border table-striped table-bordered align-middle">
                                    <thead>
                                        <tr>
                                            <th>S.No</th>
                                            <th>Banner Image</th>
                                            <th>Sub Category</th>
                                            <th className="text-center">
                                                Action
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="text-center py-4"
                                                >
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    />
                                                    Loading banners…
                                                </td>
                                            </tr>
                                        ) : banners.length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="text-center fw-bold py-4"
                                                >
                                                    No banners added yet.
                                                </td>
                                            </tr>
                                        ) : (
                                            banners.map((banner, index) => {
                                                const bannerId =
                                                    banner.id ?? banner.banner_id;

                                                const categoryName =
                                                    occasionSubcategories.find(
                                                        (sub) =>
                                                            String(sub.sub_id) ===
                                                            String(banner.sub_id)
                                                    )?.sub_cate || "N/A";

                                                const imageUrl = banner.image
                                                    ? `${API_BASE}/uploads/section-banners/${encodeURIComponent(
                                                          banner.image
                                                      )}`
                                                    : null;

                                                const isDeleting =
                                                    deletingIds.some(
                                                        (id) =>
                                                            String(id) ===
                                                            String(bannerId)
                                                    );

                                                return (
                                                    <tr
                                                        key={
                                                            bannerId ??
                                                            `banner-${index}`
                                                        }
                                                    >
                                                        <td>{index + 1}</td>

                                                        <td>
                                                            {imageUrl ? (
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={`Banner ${index + 1}`}
                                                                    loading="lazy"
                                                                    style={{
                                                                        width: "100px",
                                                                        height: "60px",
                                                                        objectFit:
                                                                            "cover",
                                                                        borderRadius:
                                                                            "6px",
                                                                        border: "1px solid #dee2e6",
                                                                    }}
                                                                    onError={(
                                                                        event
                                                                    ) => {
                                                                        event.currentTarget.onerror =
                                                                            null;
                                                                        event.currentTarget.src =
                                                                            "/customer_data/no_image.webp";
                                                                    }}
                                                                />
                                                            ) : (
                                                                <span className="text-muted">
                                                                    No image
                                                                </span>
                                                            )}
                                                        </td>

                                                        <td>
                                                            <span className="badge bg-light-primary text-primary">
                                                                {categoryName}
                                                            </span>
                                                        </td>

                                                        <td className="text-center">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                aria-label={`Delete banner ${index + 1}`}
                                                                disabled={
                                                                    isDeleting ||
                                                                    bannerId == null
                                                                }
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        bannerId
                                                                    )
                                                                }
                                                            >
                                                                {isDeleting ? (
                                                                    <>
                                                                        <span
                                                                            className="spinner-border spinner-border-sm me-1"
                                                                            role="status"
                                                                            aria-hidden="true"
                                                                        />
                                                                        Removing…
                                                                    </>
                                                                ) : (
                                                                    <i className="ti ti-trash" />
                                                                )}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultipleBanner;